import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang, useCart } from "../context";
import { t } from "../content";
import { http } from "../api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const L = {
  name: { fr: "Nom complet", en: "Full name" },
  email: { fr: "Courriel", en: "Email" },
  phone: { fr: "Téléphone (optionnel)", en: "Phone (optional)" },
  address: { fr: "Adresse", en: "Address" },
  city: { fr: "Ville", en: "City" },
  province: { fr: "Province", en: "Province" },
  postal: { fr: "Code postal", en: "Postal code" },
  country: { fr: "Pays", en: "Country" },
  shipTitle: { fr: "Adresse de livraison", en: "Shipping address" },
  required: { fr: "Veuillez remplir tous les champs obligatoires.", en: "Please fill all required fields." },
};

export default function Cart() {
  const { lang } = useLang();
  const { cart, removeFromCart, updateQty } = useCart();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", province: "", postal_code: "", country: "Canada" });
  const subtotal = cart.reduce((a, i) => a + i.price * i.quantity, 0);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name && form.email && form.address && form.city && form.province && form.postal_code && form.country;

  const checkout = async () => {
    if (!valid) { toast.error(L.required[lang]); return; }
    setLoading(true);
    try {
      const { data } = await http.post("/checkout/session", {
        items: cart.map((i) => ({ id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer: form,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur lors du paiement." : "Checkout error.");
      setLoading(false);
    }
  };

  if (cart.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="serif text-4xl">{t.cart.title[lang]}</h1>
        <p className="text-[#737373] mt-4">{t.cart.empty[lang]}</p>
        <Link to="/shop" data-testid="empty-continue" className="inline-block mt-8 bg-[#1A1A1A] text-[#F9F8F6] px-7 py-3.5 text-sm uppercase tracking-widest rounded-sm">{t.cart.continue[lang]}</Link>
      </div>
    );

  const field = (key, span = 1, type = "text") => (
    <input required type={type} value={form[key === "postal" ? "postal_code" : key]} data-testid={`ship-${key}`}
      onChange={set(key === "postal" ? "postal_code" : key)} placeholder={L[key][lang]}
      className={`col-span-${span} bg-transparent border-b border-[#1A1A1A]/25 py-2.5 text-sm outline-none focus:border-[#1A1A1A] transition-colors`} />
  );

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-12">
      <h1 className="serif text-5xl mb-10">{t.cart.title[lang]}</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="divide-y divide-[#1A1A1A]/10">
            {cart.map((i) => (
              <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-5 py-6">
                <div className="w-24 h-32 bg-[#F0EBE1] flex-shrink-0"><img src={i.image} alt={i[`name_${lang}`]} className="w-full h-full object-cover" /></div>
                <div className="flex-1">
                  <h3 className="serif text-lg">{i[`name_${lang}`]}{i.size ? ` - ${i.size}` : ""}</h3>
                  <p className="text-sm text-[#737373] mt-1">{i.price.toFixed(2)} $ CAD</p>
                  {i.is_unique ? (
                    <p className="mt-3 text-xs uppercase tracking-widest text-[#737373]" data-testid={`unique-note-${i.id}`}>
                      {lang === "fr" ? "Pièce unique · Qté 1" : "One of one · Qty 1"}
                    </p>
                  ) : (
                    <div className="mt-3 flex items-center gap-3">
                      <button onClick={() => updateQty(i.id, i.quantity - 1)} data-testid={`qty-minus-${i.id}`} className="w-7 h-7 border border-[#1A1A1A]/20 rounded-sm">−</button>
                      <span data-testid={`qty-${i.id}`} className="text-sm w-6 text-center">{i.quantity}</span>
                      <button onClick={() => updateQty(i.id, i.quantity + 1)} data-testid={`qty-plus-${i.id}`} className="w-7 h-7 border border-[#1A1A1A]/20 rounded-sm">+</button>
                    </div>
                  )}
                </div>
                <button onClick={() => removeFromCart(i.id)} data-testid={`remove-${i.id}`} className="text-[#737373] hover:text-[#1A1A1A] self-start"><Trash2 size={18} strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <h2 className="serif text-2xl mb-5">{L.shipTitle[lang]}</h2>
            <div className="grid grid-cols-2 gap-x-5 gap-y-1">
              {field("name", 2)}
              {field("email", 2, "email")}
              {field("phone", 2)}
              {field("address", 2)}
              {field("city")}
              {field("province")}
              {field("postal")}
              {field("country")}
            </div>
          </div>
        </div>

        <div className="bg-[#F0EBE1] p-7 h-fit rounded-sm lg:sticky lg:top-24">
          <div className="flex justify-between text-sm mb-2"><span>{t.cart.subtotal[lang]}</span><span data-testid="cart-subtotal">{subtotal.toFixed(2)} $ CAD</span></div>
          <p className="text-xs text-[#737373] mb-6">{t.cart.shipping[lang]}</p>
          <button onClick={checkout} disabled={loading} data-testid="checkout-btn" className="w-full bg-[#1A1A1A] text-[#F9F8F6] py-4 text-sm uppercase tracking-widest rounded-sm disabled:opacity-60">
            {loading ? "…" : t.cart.checkout[lang]}
          </button>
        </div>
      </div>
    </div>
  );
}
